import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { examComponentsAPI } from '../../services/api';
import './ExamComponentUploads.css';

const toDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const examTypeLabel = (value) => {
  if (value === 'beroepsproduct') return 'Beroepsproduct';
  if (value === 'adviesrapport') return 'Adviesrapport';
  if (value === 'schriftelijke-opdracht') return 'Schriftelijke opdracht';
  return value || '-';
};

const reviewStatusLabel = (value) => {
  if (value === 'reviewed') return 'Beoordeeld';
  return 'Open';
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'exam-inzendingen.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ExamComponentUploads = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [downloadingGroupKey, setDownloadingGroupKey] = useState('');
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');

  const [filterCourse, setFilterCourse] = useState('');
  const [filterExamType, setFilterExamType] = useState('');
  const [filterReviewStatus, setFilterReviewStatus] = useState('');

  const [reviewForms, setReviewForms] = useState({});

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await examComponentsAPI.getAdminSubmissions({
        ...(filterCourse ? { courseId: filterCourse } : {}),
        ...(filterExamType ? { examType: filterExamType } : {}),
        ...(filterReviewStatus ? { reviewStatus: filterReviewStatus } : {})
      });
      const nextItems = response.items || [];
      setItems(nextItems);
      setReviewForms((prev) => {
        const next = { ...prev };
        nextItems.forEach((item) => {
          const key = String(item.id);
          if (!next[key]) {
            next[key] = {
              score: item.score ?? '',
              feedback: item.feedback || '',
              reviewStatus: item.reviewStatus || 'pending'
            };
          }
        });
        return next;
      });
    } catch (loadError) {
      setError(loadError.message || 'Laden van examenuploads mislukt');
    } finally {
      setLoading(false);
    }
  }, [filterCourse, filterExamType, filterReviewStatus]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const courseOptions = useMemo(() => {
    const map = new Map();
    (items || []).forEach((item) => {
      const id = String(item.course?.id || '');
      if (!id) return;
      if (!map.has(id)) {
        const label = item.course?.code ? `${item.course.code} - ${item.course.name}` : (item.course?.name || '-');
        map.set(id, { id, label });
      }
    });
    return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label, 'nl'));
  }, [items]);

  const groupedByExamMoment = useMemo(() => {
    const groups = new Map();
    (items || []).forEach((item) => {
      const key = `${item.course?.id || '-'}::${item.scheduleEvent?.id || '-'}::${item.examType || '-'}`;
      const current = groups.get(key) || {
        key,
        course: item.course,
        scheduleEvent: item.scheduleEvent,
        examType: item.examType,
        items: []
      };
      current.items.push(item);
      groups.set(key, current);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((left, right) => String(left.student?.name || '').localeCompare(String(right.student?.name || ''), 'nl'))
      }))
      .sort((left, right) => {
        const leftDate = new Date(left.scheduleEvent?.startDateTime || 0).getTime();
        const rightDate = new Date(right.scheduleEvent?.startDateTime || 0).getTime();
        return rightDate - leftDate;
      });
  }, [items]);

  const updateReviewForm = (id, patch) => {
    const key = String(id);
    setReviewForms((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...patch
      }
    }));
  };

  const submitReview = async (id) => {
    const key = String(id);
    const form = reviewForms[key] || {};

    setSavingId(key);
    setError('');
    setOkMessage('');
    try {
      await examComponentsAPI.reviewSubmission(key, {
        score: form.score === '' ? null : Number(form.score),
        feedback: form.feedback || '',
        reviewStatus: form.reviewStatus || 'pending'
      });
      setOkMessage('Beoordeling opgeslagen');
      await loadItems();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan beoordeling mislukt');
    } finally {
      setSavingId('');
    }
  };

  const downloadGroupZip = async (group) => {
    if (!group?.course?.id || !group?.scheduleEvent?.id || !group?.examType) {
      setError('Onvoldoende gegevens om zip te downloaden.');
      return;
    }

    setDownloadingGroupKey(group.key);
    setError('');
    setOkMessage('');
    try {
      const { blob, filename } = await examComponentsAPI.downloadAdminSubmissionsZip({
        courseId: group.course.id,
        scheduleEventId: group.scheduleEvent.id,
        examType: group.examType
      });
      downloadBlob(blob, filename || 'exam-inzendingen.zip');
      setOkMessage('ZIP met inzendingen is gedownload.');
    } catch (downloadError) {
      setError(downloadError.message || 'Downloaden van zip mislukt');
    } finally {
      setDownloadingGroupKey('');
    }
  };

  return (
    <div className="exam-uploads-page">
      <div className="page-header">
        <h1>Examenuploads</h1>
        <p className="page-subtitle">Overzicht en beoordeling van beroepsproduct, adviesrapport en schriftelijke opdracht per vak en examenmoment.</p>
      </div>

      {error && <p className="message error">{error}</p>}
      {okMessage && <p className="message ok">{okMessage}</p>}

      <section className="exam-uploads-section">
        <div className="exam-filter-panel">
          <div className="exam-filter-group">
            <label>Vak</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}>
              <option value="">Alle vakken</option>
              {courseOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="exam-filter-group">
            <label>Examenonderdeel</label>
            <select value={filterExamType} onChange={(e) => setFilterExamType(e.target.value)}>
              <option value="">Alles</option>
              <option value="beroepsproduct">Beroepsproduct</option>
              <option value="adviesrapport">Adviesrapport</option>
              <option value="schriftelijke-opdracht">Schriftelijke opdracht</option>
            </select>
          </div>

          <div className="exam-filter-group">
            <label>Status</label>
            <select value={filterReviewStatus} onChange={(e) => setFilterReviewStatus(e.target.value)}>
              <option value="">Alles</option>
              <option value="pending">Open</option>
              <option value="reviewed">Beoordeeld</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p>Laden...</p>
        ) : groupedByExamMoment.length === 0 ? (
          <p>Geen examenuploads gevonden.</p>
        ) : (
          <div className="exam-moment-list">
            {groupedByExamMoment.map((group) => (
              <article key={group.key} className="exam-moment-card">
                <div className="exam-moment-header">
                  <h2>{examTypeLabel(group.examType)}</h2>
                  <span className="exam-moment-date">{toDateTime(group.scheduleEvent?.startDateTime)}</span>
                </div>
                <div className="form-actions" style={{ marginBottom: '10px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={downloadingGroupKey === group.key}
                    onClick={() => downloadGroupZip(group)}
                  >
                    {downloadingGroupKey === group.key ? 'ZIP downloaden...' : 'Download alle inzendingen (ZIP)'}
                  </button>
                </div>
                <p className="exam-moment-meta">
                  Vak: {group.course?.code ? `${group.course.code} - ${group.course.name}` : (group.course?.name || '-')} | 
                  Kalender: {group.scheduleEvent?.title || '-'}
                </p>

                <div className="exam-submissions-grid">
                  {group.items.map((item) => {
                    const key = String(item.id);
                    const form = reviewForms[key] || { score: '', feedback: '', reviewStatus: 'pending' };
                    return (
                      <div key={item.id} className="exam-submission-card">
                        <div className="exam-submission-top">
                          <strong>{item.student?.name || '-'}</strong>
                          <span className={`status-pill ${item.reviewStatus === 'reviewed' ? 'active' : 'inactive'}`}>
                            {reviewStatusLabel(item.reviewStatus)}
                          </span>
                        </div>
                        <p className="small">Email: {item.student?.email || '-'}</p>
                        <p className="small">Ingediend: {toDateTime(item.submittedAt)}</p>

                        <div className="form-actions">
                          {item.file?.url ? (
                            <>
                              <a className="btn-secondary" href={item.file.url} target="_blank" rel="noreferrer">
                                Bekijk inzending
                              </a>
                              <a className="btn-secondary" href={item.file.url} download>
                                Download bestand
                              </a>
                            </>
                          ) : (
                            <span className="hint small">Geen bestand-url beschikbaar</span>
                          )}
                        </div>

                        <div className="exam-review-form">
                          <label>Score (0-10)</label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={form.score}
                            onChange={(e) => updateReviewForm(item.id, { score: e.target.value })}
                          />

                          <label>Status</label>
                          <select
                            value={form.reviewStatus}
                            onChange={(e) => updateReviewForm(item.id, { reviewStatus: e.target.value })}
                          >
                            <option value="pending">Open</option>
                            <option value="reviewed">Beoordeeld</option>
                          </select>

                          <label>Feedback</label>
                          <textarea
                            rows={3}
                            value={form.feedback}
                            onChange={(e) => updateReviewForm(item.id, { feedback: e.target.value })}
                          />

                          <button
                            type="button"
                            className="btn-primary"
                            disabled={savingId === key}
                            onClick={() => submitReview(item.id)}
                          >
                            {savingId === key ? 'Opslaan...' : 'Beoordeling opslaan'}
                          </button>

                          {item.reviewedBy?.name && (
                            <p className="hint small">Laatst beoordeeld door {item.reviewedBy.name} op {toDateTime(item.reviewedAt)}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ExamComponentUploads;
